import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform, useSpring } from 'framer-motion';
import { SmokeBackground } from '../components/ui/spooky-smoke-animation';

import {
  FiPhone,
  FiMail,
  FiMapPin,
  FiClock,
  FiSend,
  FiUser,
  FiMessageSquare,
  FiInstagram,
  FiFacebook,
  FiTwitter,
  FiCheck,
  FiAlertCircle
} from 'react-icons/fi';

const CONTACT_FIELD_LIMITS = {
  name: 100,
  email: 254,
  company: 120,
  message: 1000
};

const formatBrazilianMobilePhone = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);

  if (digits.length === 0) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 3) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 7) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 3)} ${digits.slice(3)}`;
  }

  return `(${digits.slice(0, 2)}) ${digits.slice(2, 3)} ${digits.slice(3, 7)}-${digits.slice(7)}`;
};

// ========= EXTRACTED COMPONENT (Rules of Hooks compliant) =========

const ContactInfoCard = ({ item, index, scrollYProgress }) => {
  const cardY = useTransform(
    scrollYProgress,
    [0, 0.3],
    [30 + index * 10, 0]
  );
  const smoothCardY = useSpring(cardY, { stiffness: 80, damping: 25 });

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: index * 0.1 }}
      style={{ y: smoothCardY }}
      className="bg-white p-8 rounded-2xl shadow-paper border border-gray-100 text-center card-hover"
    >
      <div className="inline-flex p-4 bg-gray-100 rounded-xl text-gray-900 mb-6">
        {item.icon}
      </div>
      <h3 className="text-lg font-bold text-gray-900 mb-3 tracking-tight">{item.title}</h3>
      <p className="text-gray-900 font-semibold mb-2 text-sm">{item.info}</p>
      <p className="text-gray-400 text-xs uppercase tracking-wide">{item.subInfo}</p>
    </motion.div>
  );
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
    hp: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState({});
  const location = useLocation();

  // ========= PARALLAX REFS & SCROLL =========
  const heroRef = useRef(null);
  const cardsRef = useRef(null);
  const formRef = useRef(null);

  // Hero parallax
  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroTextY = useTransform(heroProgress, [0, 1], [0, 120]);
  const heroTextOpacity = useTransform(heroProgress, [0, 0.6], [1, 0]);
  const heroScale = useTransform(heroProgress, [0, 0.5], [1, 0.95]);

  // Cards parallax
  const { scrollYProgress: cardsProgress } = useScroll({
    target: cardsRef,
    offset: ["start end", "end start"],
  });

  // Form parallax
  const { scrollYProgress: formProgress } = useScroll({
    target: formRef,
    offset: ["start end", "end start"],
  });
  const formX = useTransform(formProgress, [0, 0.5], [-30, 0]);
  const infoX = useTransform(formProgress, [0, 0.5], [30, 0]);
  const smoothFormX = useSpring(formX, { stiffness: 80, damping: 25 });
  const smoothInfoX = useSpring(infoX, { stiffness: 80, damping: 25 });

  const validateForm = () => {
    const newErrors = {};

    // Validar nome
    if (!formData.name.trim()) {
      newErrors.name = 'Por favor, informe seu nome completo';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Nome deve ter pelo menos 2 caracteres';
    } else if (formData.name.length > CONTACT_FIELD_LIMITS.name) {
      newErrors.name = `Nome deve ter no máximo ${CONTACT_FIELD_LIMITS.name} caracteres`;
    }

    // Validar email
    if (!formData.email.trim()) {
      newErrors.email = 'Por favor, informe seu email';
    } else if (formData.email.length > CONTACT_FIELD_LIMITS.email) {
      newErrors.email = `Email deve ter no máximo ${CONTACT_FIELD_LIMITS.email} caracteres`;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Por favor, informe um email válido';
    }

    if (formData.company.length > CONTACT_FIELD_LIMITS.company) {
      newErrors.company = `Empresa deve ter no máximo ${CONTACT_FIELD_LIMITS.company} caracteres`;
    }

    // Validar celular brasileiro quando informado: DDD + 9 dígitos
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits && !/^\d{2}9\d{8}$/.test(phoneDigits)) {
      newErrors.phone = 'Informe um celular válido com DDD e 9 dígitos';
    }

    // Validar mensagem
    if (!formData.message.trim()) {
      newErrors.message = 'Por favor, escreva sua mensagem';
    } else if (formData.message.trim().length < 1) {
      newErrors.message = 'Mensagem deve ter pelo menos 1 caractere';
    } else if (formData.message.length > CONTACT_FIELD_LIMITS.message) {
      newErrors.message = `Mensagem deve ter no máximo ${CONTACT_FIELD_LIMITS.message} caracteres`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const fieldLimit = CONTACT_FIELD_LIMITS[name];
    const normalizedValue = name === 'phone'
      ? formatBrazilianMobilePhone(value)
      : fieldLimit
        ? value.slice(0, fieldLimit)
        : value;

    setFormData({
      ...formData,
      [name]: normalizedValue
    });

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar formulário antes de enviar
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitted(true);
      // Enviar para nosso endpoint interno (Express + Nodemailer)
      const payload = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone || '-',
        company: formData.company || '-',
        service: formData.service || '-',
        message: formData.message,
        subject: `Novo contato pelo site | ${formData.service || 'Assunto geral'}`,
        to: process.env.REACT_APP_EMAIL_TO || 'contato@aytech.online',
        hp: formData.hp || ''
      };

      const response = await fetch(`/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'contact_form_success',
          form_name: 'contact',
          service: formData.service || 'not_informed'
        });

        // Sucesso - limpar formulário
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          message: '',
          hp: ''
        });
        setErrors({});
        setShowConfirm(true)
        // Mostrar estados por alguns segundos
        setTimeout(() => setIsSubmitted(false), 5000)
        setTimeout(() => setShowConfirm(false), 6000)
      } else {
        const result = await response.json().catch(() => ({}));
        console.error('Erro ao enviar para backend interno:', result);
        setIsSubmitted(false);
      }
    } catch (error) {
      // Erro de rede ou outro erro
      console.error('Erro ao enviar formulário:', error);
      setIsSubmitted(false);
    }
  };

  const contactInfo = [
    {
      icon: <FiPhone className="w-6 h-6" />,
      title: "Telefone",
      info: "(41) 9 8771-8296",
      subInfo: "WhatsApp disponível"
    },
    {
      icon: <FiMail className="w-6 h-6" />,
      title: "Email",
      info: "contato@aytech.online",
      subInfo: "Resposta em até 2 horas"
    },
    {
      icon: <FiMapPin className="w-6 h-6" />,
      title: "Endereço",
      info: "Curitiba",
      subInfo: "Paraná"
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: "Horário",
      info: "24/7",
      subInfo: "Respondemos o mais rápido possível"
    }
  ];

  const services = React.useMemo(() => [
    "PapIA - Automação de IA",
    "Rotas - Gestão de Entregas",
    "Lucrum - Sistema Financeiro",
    "Delivex - Gestão de Delivery",
    "Outro"
  ], []);

  const socialLinks = [
    { icon: <FiInstagram />, url: 'https://www.instagram.com/aytechcwb/', name: 'Instagram' },
    { icon: <FiFacebook />, url: 'https://www.facebook.com/people/AyTech/61576927466865/', name: 'Facebook' },
    { icon: <FiTwitter />, url: 'https://x.com/Aytechcwb', name: 'Twitter' }
  ];


  // Pré-selecionar serviço vindo da query string (?service=...)
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const raw = params.get('service');
      if (!raw) return;

      const q = decodeURIComponent(raw).trim();
      const normalized = q.toLowerCase();

      const synonymsMap = {
        'automação de ia': 'PapIA - Automação de IA',
        'sintia': 'PapIA - Automação de IA',
        'sintia - automação de ia': 'PapIA - Automação de IA',
        'papia': 'PapIA - Automação de IA',
        'papia - automação de ia': 'PapIA - Automação de IA',
        'rotas': 'Rotas - Gestão de Entregas',
        'rotas inteligentes': 'Rotas - Gestão de Entregas',
        'criador de rotas': 'Rotas - Gestão de Entregas',
        'rotas - gestão de entregas': 'Rotas - Gestão de Entregas',
        'lucrum': 'Lucrum - Sistema Financeiro',
        'sistema financeiro': 'Lucrum - Sistema Financeiro',
        'lucrum - sistema financeiro': 'Lucrum - Sistema Financeiro',
        'delivex': 'Delivex - Gestão de Delivery',
        'gestão de delivery': 'Delivex - Gestão de Delivery',
        'delivex - gestão de delivery': 'Delivex - Gestão de Delivery',
        'outro': 'Outro'
      };

      const exact = services.find(s => s.toLowerCase() === normalized);
      const includes = services.find(s => s.toLowerCase().includes(normalized));
      const synonym = synonymsMap[normalized];
      const resolved = exact || includes || synonym || 'Outro';

      if (resolved) {
        // Atualiza somente se o valor for diferente para evitar loops
        setFormData(prev => (
          prev.service === resolved ? prev : { ...prev, service: resolved }
        ));
      }
    } catch (e) {
      // Silenciar erros de parsing
    }
  }, [location.search, services]);

  return (<>
    <div className="min-h-screen">
      {/* Hero Section */}
      <section ref={heroRef} className="pt-44 pb-44 parallax-section bg-gray-950" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="pointer-events-none absolute inset-0 z-0" aria-hidden="true">
          <SmokeBackground smokeColor="#2f2f2f" />
        </div>
        <div className="pointer-events-none absolute inset-0 z-0 parallax-grid opacity-20" aria-hidden="true" />

        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10"
          style={{
            y: heroTextY,
            opacity: heroTextOpacity,
            scale: heroScale,
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
          >
            <span className="text-xs text-white/70 uppercase tracking-widest font-mono">Fale com a gente</span>
            <h1 className="text-5xl md:text-7xl font-display font-bold text-white mt-4 mb-6 tracking-tighter leading-tight">
              Entre em Contato
            </h1>
            <p className="text-lg text-white/70 max-w-2xl leading-relaxed">
              Estamos prontos para transformar suas ideias em soluções tecnológicas inovadoras.
              Fale conosco e vamos começar essa jornada juntos.
            </p>
          </motion.div>
        </motion.div>

        {/* Organic transition into the contact cards section */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-16 leading-[0] sm:h-20 lg:h-24"
          aria-hidden="true"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1000 100"
            preserveAspectRatio="none"
            className="block h-full w-full scale-y-[-1]"
          >
            <g fill="#ffffff">
              <path d="M0 0v99.7C62 69 122.4 48.7 205 66c83.8 17.6 160.5 20.4 240-12 54-22 110-26 173-10a392.2 392.2 0 0 0 222-5c55-17 110.3-36.9 160-27.2V0H0Z" opacity=".5" />
              <path d="M0 0v74.7C62 44 122.4 28.7 205 46c83.8 17.6 160.5 25.4 240-7 54-22 110-21 173-5 76.5 19.4 146.5 23.3 222 0 55-17 110.3-31.9 160-22.2V0H0Z" />
            </g>
          </svg>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section ref={cardsRef} className="-mt-px py-20 bg-white parallax-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 -mt-20 relative z-10">
            {contactInfo.map((item, index) => (
              <ContactInfoCard
                key={index}
                item={item}
                index={index}
                scrollYProgress={cardsProgress}
              />
            ))}
          </div>

          {/* Honeypot anti-spam: campo oculto invisível para humanos */}
          <input
            type="text"
            name="hp"
            value={formData.hp}
            onChange={handleInputChange}
            className="hidden"
            tabIndex={-1}
            aria-hidden="true"
          />
        </div>
      </section>

      {/* Contact Form & Info */}
      <section ref={formRef} className="py-20 bg-gray-50 parallax-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form with lateral parallax */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              style={{ x: smoothFormX }}
              className="bg-white p-8 rounded-2xl shadow-paper border border-gray-100"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6 tracking-tight">
                Envie sua Mensagem
              </h2>
              <p className="text-gray-600 mb-8">
                Preencha o formulário abaixo e nossa equipe entrará em contato em até 2 horas.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome Completo *
                    </label>
                    <div className="relative">
                      <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        maxLength={CONTACT_FIELD_LIMITS.name}
                        className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${errors.name
                            ? 'border-red-500 focus:ring-red-500'
                            : 'border-gray-200 focus:ring-gray-900'
                          }`}
                        placeholder="Seu nome"
                      />
                    </div>
                    {errors.name && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-2 text-sm text-red-600 flex items-center"
                      >
                        <FiAlertCircle className="w-4 h-4 mr-1" />
                        {errors.name}
                      </motion.p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <div className="relative">
                      <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        maxLength={CONTACT_FIELD_LIMITS.email}
                        className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${errors.email
                            ? 'border-red-500 focus:ring-red-500'
                            : 'border-gray-200 focus:ring-gray-900'
                          }`}
                        placeholder="seu@email.com"
                      />
                    </div>
                    {errors.email && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-2 text-sm text-red-600 flex items-center"
                      >
                        <FiAlertCircle className="w-4 h-4 mr-1" />
                        {errors.email}
                      </motion.p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefone
                    </label>
                    <div className="relative">
                      <FiPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        inputMode="numeric"
                        maxLength={16}
                        autoComplete="tel-national"
                        className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${errors.phone
                            ? 'border-red-500 focus:ring-red-500'
                            : 'border-gray-200 focus:ring-gray-900'
                          }`}
                        placeholder="(99) 9 9999-9999"
                      />
                    </div>
                    {errors.phone && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-2 text-sm text-red-600 flex items-center"
                      >
                        <FiAlertCircle className="w-4 h-4 mr-1" />
                        {errors.phone}
                      </motion.p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Empresa
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      maxLength={CONTACT_FIELD_LIMITS.company}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${errors.company
                          ? 'border-red-500 focus:ring-red-500'
                          : 'border-gray-200 focus:ring-gray-900'
                        }`}
                      placeholder="Nome da sua empresa"
                    />
                    {errors.company && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-2 text-sm text-red-600 flex items-center"
                      >
                        <FiAlertCircle className="w-4 h-4 mr-1" />
                        {errors.company}
                      </motion.p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Serviço de Interesse
                  </label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  >
                    <option value="">Selecione um serviço</option>
                    {services.map((service, index) => (
                      <option key={index} value={service}>{service}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mensagem *
                  </label>
                  <div className="relative">
                    <FiMessageSquare className="absolute left-3 top-3 text-gray-400" />
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={5}
                      maxLength={CONTACT_FIELD_LIMITS.message}
                      spellCheck={false}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent resize-none ${errors.message
                          ? 'border-red-500 focus:ring-red-500'
                          : 'border-gray-200 focus:ring-gray-900'
                        }`}
                      placeholder="Conte-nos sobre seu projeto ou necessidade..."
                    />
                  </div>
                  <p className="mt-1 text-right text-xs text-gray-400">
                    {formData.message.length}/{CONTACT_FIELD_LIMITS.message}
                  </p>
                  {errors.message && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-2 text-sm text-red-600 flex items-center"
                    >
                      <FiAlertCircle className="w-4 h-4 mr-1" />
                      {errors.message}
                    </motion.p>
                  )}
                </div>

                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`w-full py-4 rounded-full font-semibold text-sm uppercase tracking-widest transition-all duration-300 flex items-center justify-center ${isSubmitted
                      ? 'bg-gray-500 text-white'
                      : 'bg-gray-950 hover:bg-gray-700 text-white btn-hover'
                    }`}
                  style={!isSubmitted ? { backgroundColor: '#0a0a0a' } : {}}
                  disabled={isSubmitted}
                >
                  {isSubmitted ? (
                    <>
                      <FiCheck className="mr-2" />
                      Mensagem Enviada!
                    </>
                  ) : (
                    <>
                      <FiSend className="mr-2" />
                      Solicitar Contato Gratuito
                    </>
                  )}
                </motion.button>

                {/* Privacy Reassurance */}
                <p className="mt-4 text-center text-sm text-gray-500">
                  🔒 Suas informações estão seguras e não serão compartilhadas com terceiros.
                </p>
              </form>
            </motion.div>

            {/* Contact Information with lateral parallax */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              style={{ x: smoothInfoX }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6 tracking-tight">
                  Vamos Conversar?
                </h2>
                <p className="text-gray-600 text-lg leading-relaxed mb-8">
                  Estamos sempre disponíveis para discutir como nossas soluções podem
                  transformar seu negócio. Entre em contato conosco através dos canais abaixo
                  ou preencha o formulário ao lado.
                </p>
              </div>

              {/* Contact Details */}
              <div className="bg-white p-8 rounded-2xl shadow-paper border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-6 tracking-tight uppercase tracking-widest text-sm">Informações de Contato</h3>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <FiMapPin className="w-5 h-5 text-gray-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm">Endereço</h4>
                      <p className="text-gray-500 text-sm">Curitiba, Paraná</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <FiPhone className="w-5 h-5 text-gray-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm">Telefone & WhatsApp</h4>
                      <p className="text-gray-500 text-sm">(41) 9 8771-8296</p>
                      <p className="text-xs text-gray-400">Apenas WhatsApp, ligações não são respondidas</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <FiMail className="w-5 h-5 text-gray-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm">Email</h4>
                      <p className="text-gray-500 text-sm">contato@aytech.online</p>
                      <p className="text-xs text-gray-400">Resposta em até 2 horas</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="bg-gray-950 p-8 rounded-2xl text-white" style={{ backgroundColor: '#0a0a0a' }}>
                <h3 className="text-sm font-semibold mb-6 uppercase tracking-widest text-gray-300">Siga-nos nas Redes Sociais</h3>
                <div className="flex space-x-3">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.1, y: -2 }}
                      className="w-10 h-10 bg-white/5 hover:bg-white/15 border border-white/10 rounded-lg flex items-center justify-center transition-all duration-300 text-gray-400 hover:text-white"
                    >
                      {social.icon}
                    </motion.a>
                  ))}
                </div>
                <p className="text-gray-500 mt-4 text-sm">
                  Acompanhe nossas novidades, dicas e cases de sucesso.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>

    <AnimatePresence>
      {showConfirm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowConfirm(false)}
        >
          <motion.div
            initial={{ scale: 0.95, y: 30, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.96, y: 20, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 24 }}
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col items-center text-center space-y-3">
              <div className="w-12 h-12 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
                <FiCheck className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Agradecemos seu contato!</h3>
              <p className="text-gray-600">Responderemos assim que possível.</p>
              <button
                onClick={() => setShowConfirm(false)}
                className="mt-2 inline-flex items-center px-4 py-2 rounded-full bg-gray-900 hover:bg-gray-700 text-white font-semibold transition-colors duration-300 text-sm"
              >
                Fechar
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  </>
  );
};


export default Contact;
